import { loadProfileOptions } from '../../services/profileOptionsService';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../supabaseClient.js';
import Grid from '@mui/material/Grid2';
import { IconButton, Typography, MenuItem, Select, FormControl, InputLabel, TextField, Stack, Button, Box, FormControlLabel, InputAdornment, Tooltip, CircularProgress, Slider, Alert, LinearProgress, Switch, Autocomplete } from '@mui/material';
import useAlert from '../../alert/useAlert.js';
import { setUserDetails } from '../../store.js';
import { useDispatch } from 'react-redux';
import SearchableMultiSelect from '../../components/SearchableMultiSelect';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MENTAL_HEALTH_SPECIALITIES, SERVICE_FORMAT_OPTIONS, DYNAMIC_OPTION_TYPES, OTHER_OPTION, CONSULTATION_SOFTWARE, LANGUAGES, PROFESSIONAL_QUALIFICATIONS, withAgeRange } from '../../constants/profileOptions.js';
//import { HKPS_REGISTERED_NUMS, HKICP_REGISTERED_NUMS, HKAEP_REGISTERED_NUMS } from '../../constants/psychologistID.js';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ClinicLocationManager from '../../components/ClinicLocationManager.js';
import ProfessionalVerificationForm, { deriveKey, encryptFile } from '../../components/ProfessionalVerificationForm.js';
import { useSelector, shallowEqual } from 'react-redux';
import { set } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';
import Loader from '../../loader.js';
import { getTeamLocations } from '../../services/organizationService.js';
import PayoutSetup from './PayoutSetup.js';
import { getMissingProfileFields } from '../../utils/profileCompleteness.js';
import countries from '../../constants/countries.json';
import ProfileGuide from '../../components/ProfileGuide';
import SessionPackages from './SessionPackages/SessionPackages';
import './profileSections.css';
import MindVaultProfilePreview from './MindVaultProfilePreview';
import ProfileAutofill from '../../components/ProfileAutofill';
import { buildPrefillOptionLists, isBlankValue, registryPrefillFields, REGISTRY_KEYS } from '../../utils/profilePrefill.js';

const COUNTRY_NAMES = countries.map((c) => c.name);
const RELIGION_OPTIONS = [
  'Buddhist',
  'Christian / Catholic',
  'Hindu',
  'Muslim',
  'Jewish',
  'Sikh',
  'No religion',
  'Prefer not to say',
  'Other',
];

const UpdateProfile = ({userid, useremail, username, userpracticetype, data_retention_days, showWelcome, onOpenAccountSettings}) => {
  const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
  const allOrgs = useSelector((state) => state.globalState.allOrgs, shallowEqual);
  const [name, setName] = useState(username);
  const [practiceType, setPracticeType] = useState(userpracticetype);
  // Mode is no longer user-selectable — the product is Mental Health only
  // (onboarding stamps the same fixed value; saving here re-stamps legacy rows).
  const mode = 'Mental Health';
  const showClinicalPractice = true;

  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [about, setAbout] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const [practiceTitle, setPracticeTitle] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [country, setCountry] = useState('');
  const [address, setAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [website, setWebsite] = useState('');
  const [secondaryEmail, setSecondaryEmail] = useState('');

  // New fields
  const [gender, setGender] = useState('');
  const [languages, setLanguages] = useState([]);
  const [interventionApproach, setInterventionApproach] = useState([]);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [preferredCaseTypes, setPreferredCaseTypes] = useState([]);
  const [clientGroups, setClientGroups] = useState([]);
  const [religion, setReligion] = useState('');
  const [otherReligion, setOtherReligion] = useState('');
  const [casesToAvoid, setCasesToAvoid] = useState([]);
  const [professionalQualification, setProfessionalQualification] = useState('');
  const [university, setUniversity] = useState('');
  const [supportingDocUrl, setSupportingDocUrl] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [experienceYears, setExperienceYears] = useState('');
  const [serviceFormats, setServiceFormats] = useState([]);
  const [consultationSoftware, setConsultationSoftware] = useState([]);
  const [firstSessionDuration, setFirstSessionDuration] = useState('');
  const [firstSessionPrice, setFirstSessionPrice] = useState(10);
  const [regularSessionDuration, setRegularSessionDuration] = useState('');
  const [regularSessionPrice, setRegularSessionPrice] = useState(10);
  const [bankAccount, setBankAccount] = useState('');
  const [shareProfile, setShareProfile] = useState(false);
  const [clinicLocations, setClinicLocations] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  // Registry verification now lives in the shared ProfessionalVerificationForm;
  // sharing uses its verified flag, and Auto-fill can suggest unlocked drafts.
  const verificationRef = useRef(null);
  const [registryAutofillState, setRegistryAutofillState] = useState({ loading: true });
  const handleVerificationStatus = useCallback((verified) => {
    setRegisteredNumberVerified(verified);
    if (!verified) setShareProfile(false);
  }, []);
  const [registeredNumberVerified, setRegisteredNumberVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  const [imgOffset, setImgOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  const AVATAR_SIZE = 100;
  const [imgScale, setImgScale] = useState(1.0);

  const [profileSubmitted, setProfileSubmitted] = useState(false);
  // Stripe Connect payout status is reported up by <PayoutSetup /> for its own
  // settings UI. It is not a prerequisite for referral sharing.
  const [, setPayoutStatus] = useState(null);
  const { setAlert } = useAlert();
  const dispatch = useDispatch(); 

  const [activeSection, setActiveSection] = useState(0);
  const [savedSections, setSavedSections] = useState(null);
  const [savedSharing, setSavedSharing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const savingRef = useRef(false);
  const [packagesDirty, setPackagesDirty] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const sectionLabels = ['Basic Information', 'Professional Qualifications', 'Services & Specialties', 'Locations & Contact', 'Fees & Packages', 'Review & Share'];
  const sectionValues = [
    { name, gender, practiceType, about, country, languages, religion, otherReligion, profilePictureUrl, imgOffset, imgScale },
    { practiceTitle, organisation, university, supportingDocUrl, professionalQualification, experienceYears },
    { interventionApproach, assessmentTypes, preferredCaseTypes, clientGroups, casesToAvoid, serviceFormats, consultationSoftware },
    { clinicLocations, phoneNumber, address, website, secondaryEmail },
    { firstSessionDuration, firstSessionPrice, regularSessionDuration, regularSessionPrice, bankAccount },
  ];
  const snapshotRef = useRef(sectionValues);
  snapshotRef.current = sectionValues;
  useEffect(() => {
    if (!loading && !loadError && !savedSections) setSavedSections(snapshotRef.current);
  }, [loading, loadError, savedSections]);
  const sectionDirty = sectionValues.map((value, index) => !savedSections || JSON.stringify(value) !== JSON.stringify(savedSections[index]) || (index === 4 && packagesDirty));
  useEffect(() => {
    const reveal = event => {
      const target = event.detail?.target;
      const panel = target && document.querySelector(target)?.closest('[data-profile-section]');
      if (panel) setActiveSection(Number(panel.dataset.profileSection));
    };
    window.addEventListener('profile-reveal-target', reveal);
    return () => window.removeEventListener('profile-reveal-target', reveal);
  }, []);

  const showShareProfileSection = showClinicalPractice;

  // All profile fields (except Profile Picture) must be filled before share_profile can be enabled
  const missingShareProfileFields = getMissingProfileFields({
    name, gender, mode, practiceType, about, languages, practiceTitle, organisation,
    professionalQualification, experienceYears, interventionApproach, assessmentTypes,
    preferredCaseTypes, clientGroups, serviceFormats, firstSessionDuration, firstSessionPrice,
    regularSessionDuration, regularSessionPrice, clinicLocations, phoneNumber, address, secondaryEmail,
  });
  const profileCompleteForSharing = missingShareProfileFields.length === 0;
  const canEnableSharing = registeredNumberVerified && profileCompleteForSharing;
  const requiredGroups = [
    ['Name', 'Gender', 'Type/Speciality', 'About Me', 'Languages'],
    ['Practice Title', 'Organization', 'Professional Qualification', 'Experience (years)'],
    ['Intervention Approach', 'Assessment Types', 'Speciality Areas', 'Client Group', 'Service Format'],
    ['Clinic Locations', 'Availability', 'Phone Number', 'Address', 'Contact Email'],
    ['First Session Duration', 'First Session Price', 'Regular Session Duration', 'Regular Session Price'],
  ];
  const sectionComplete = requiredGroups.map((group, index) => !group.some(label => missingShareProfileFields.includes(label)) && (index !== 1 || registeredNumberVerified) && (index !== 4 || (Number(firstSessionPrice) >= 10 && Number(regularSessionPrice) >= 10)));
  const canEnableSavedSharing = canEnableSharing && sectionComplete.every(Boolean) && sectionDirty.every(dirty => !dirty) && !savingProfile && !uploadingImage && !uploadingDoc;
  const savedCompleteCount = sectionComplete.filter((complete, index) => complete && !sectionDirty[index]).length;


  // Sharing consent must always be an explicit user action: if any requirement
  // lapses, drop the stored consent so the checkbox never re-checks itself
  // the moment the requirements are met again.
  useEffect(() => {
    if (loading) return;
    if (!canEnableSharing && shareProfile) {
      setShareProfile(false);
    }
  }, [loading, canEnableSharing, shareProfile]);

  const filteredSpecialities = MENTAL_HEALTH_SPECIALITIES;
  
  const [optionsByType, setOptionsByType] = useState({
    [DYNAMIC_OPTION_TYPES.INTERVENTION_APPROACHES]: [],
    [DYNAMIC_OPTION_TYPES.ASSESSMENT_TYPES]: [],
    [DYNAMIC_OPTION_TYPES.PREFERRED_CASE_TYPES]: [],
    [DYNAMIC_OPTION_TYPES.CLIENT_GROUPS]: [],
    [DYNAMIC_OPTION_TYPES.AVOID_CASE_TYPES]: [],
  });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [customInputActive, setCustomInputActive] = useState({});
  const [customInputValue, setCustomInputValue] = useState({});
  const [dynamicSearchInput, setDynamicSearchInput] = useState({});
  const [optionsError, setOptionsError] = useState('');

  const fetchDynamicOptions = useCallback(async () => {
    setOptionsLoading(true);
    setOptionsError('');
    try {
      const next = await loadProfileOptions(userid);
      Object.keys(next).forEach(type => next[type].push(OTHER_OPTION));
      setOptionsByType(next);
    } catch (error) {
      setOptionsError('Could not load your profile options. Please retry.');
    } finally {
      setOptionsLoading(false);
    }
  }, [userid]);

  useEffect(() => { fetchDynamicOptions(); }, [fetchDynamicOptions]);

  // Fetch available locations from all user's organizations
  useEffect(() => {
      const fetchAllOrgLocations = async () => {
          if (!allOrgs || allOrgs.length === 0) {
              setAvailableLocations([]);
              return;
          }

          let combinedLocations = [];
          for (const org of allOrgs) {
              const { data, error } = await getTeamLocations(org.id);
              if (data && Array.isArray(data)) {
                  const orgLocs = data.map(loc => ({
                      ...loc,
                      team_id: org.id,
                      team_name: org.name
                  }));
                  combinedLocations = [...combinedLocations, ...orgLocs];
              }
          }
          setAvailableLocations(combinedLocations);
      };
      
      fetchAllOrgLocations();
  }, [allOrgs]);

  useEffect(() => {
    async function fetchPracticeInfo() {
      setLoading(true);
      setLoadError('');
      setSavedSections(null);
      try {
        const { data, error } = await supabase
          .from('user_accounts')
          .select('*')
          .eq('id', userid)
          .maybeSingle();
          
        if (error) {
          throw error;
        }
        if (!data) throw new Error('Profile could not be found.');
        if (data) {
          setName(data.name ?? username);
          setPracticeType(data.practicetype ?? userpracticetype);
          //decrypt the data
          const [
            practiceTitleResult,
            organisationResult,
            addressResult, 
            phoneNumberResult, 
            websiteResult, 
            emailResult,
            genderResult,
            religionResult,
            firstSessionPriceResult,
            regularSessionPriceResult,
            bankAccountResult,
            aboutResult
          ] = await Promise.all([
            supabase.rpc('decrypt_text', { encrypted_data: data.practice_title }),
            supabase.rpc('decrypt_text', { encrypted_data: data.organisation }),
            supabase.rpc('decrypt_text', { encrypted_data: data.address }),
            supabase.rpc('decrypt_text', { encrypted_data: data.phone_number }),
            supabase.rpc('decrypt_text', { encrypted_data: data.website }),
            supabase.rpc('decrypt_text', { encrypted_data: data.secondary_email }),
            supabase.rpc('decrypt_text', { encrypted_data: data.gender }),
            supabase.rpc('decrypt_text', { encrypted_data: data.religion }),
            supabase.rpc('decrypt_text', { encrypted_data: data.first_session_price }),
            supabase.rpc('decrypt_text', { encrypted_data: data.regular_session_price }),
            supabase.rpc('decrypt_text', { encrypted_data: data.bank_account }),
            supabase.rpc('decrypt_text', { encrypted_data: data.about }) 
          ]);
          
          if(practiceTitleResult.error || organisationResult.error || addressResult.error ||
             phoneNumberResult.error || websiteResult.error || emailResult.error ||
             genderResult.error || religionResult.error ||
             firstSessionPriceResult.error || regularSessionPriceResult.error || bankAccountResult.error || aboutResult.error) {
            throw new Error('Could not decrypt your profile.');
          }
          setPracticeTitle(practiceTitleResult.data || '');
          setOrganisation(organisationResult.data || '');
          setAddress(addressResult.data || '');
          setPhoneNumber(phoneNumberResult.data || '');
          setWebsite(websiteResult.data || '');
          setSecondaryEmail(emailResult.data || '');
          setGender(genderResult.data || '');
          setLanguages(data.languages || []);
          setInterventionApproach(data.intervention_approach || []);
          setAssessmentTypes(data.assessment_types || []);
          setPreferredCaseTypes(data.preferred_case_types || []);
          setClientGroups(data.client_groups || []);
          const savedReligion = religionResult.data || '';
          if (RELIGION_OPTIONS.includes(savedReligion)) {
            setReligion(savedReligion);
            setOtherReligion('');
          } else if (savedReligion) {
            // Keep existing free-text values editable after moving to the
            // standardised list; the database continues to store the text.
            setReligion('Other');
            setOtherReligion(savedReligion);
          } else {
            setReligion('');
            setOtherReligion('');
          }
          setCasesToAvoid(data.cases_to_avoid || []);
          setProfessionalQualification(data.professional_qualification || '');
          setServiceFormats(data.service_formats || []);
          setConsultationSoftware(data.consultation_software || []);
          setFirstSessionDuration(data.first_session_duration || '');
          setFirstSessionPrice(firstSessionPriceResult.data || 10);
          setRegularSessionDuration(data.regular_session_duration || '');
          setRegularSessionPrice(regularSessionPriceResult.data || 10);
          setBankAccount(bankAccountResult.data || '');
          setProfilePictureUrl(data.profile_picture_url || '');
          setImgOffset({
            x: parseFloat(data.profile_picture_offset_x) || 0,
            y: parseFloat(data.profile_picture_offset_y) || 0
          });
          setImgScale(parseFloat(data.profile_picture_scale) || 1.0);
          setAbout(aboutResult.data || '');
          setClinicLocations(data.clinic_locations || []);
          setExperienceYears(data.experience ?? '');
          setCountry(data.country || '');
          setUniversity(data.university || '');
          setSupportingDocUrl(data.supporting_document_url || '');

          const verified = Boolean(data.registered_number_verified);
          setRegisteredNumberVerified(verified);
          setShareProfile(verified ? Boolean(data.share_profile) : false);
          setSavedSharing(verified ? Boolean(data.share_profile) : false);
        }
      } catch (error) {
        console.error('Unexpected error fetching practice info:', error);
        setLoadError('Could not load your profile. Please retry before editing.');
      } finally {
        setLoading(false);
      }
    }
    fetchPracticeInfo();
    // Identity props can change after Update dispatches; only an account change
    // or an explicit retry should reload and replace the in-progress form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userid, loadAttempt]);

  useEffect(() => {
    setImageLoaded(false);
  }, [profilePictureUrl]);

  const startDrag = (e) => {
    if (!profilePictureUrl || !imageLoaded) return;
    if (e.pointerId) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    e.preventDefault();
    setDragStart({ x: e.clientX, y: e.clientY });
    setDragging(true);
  };

  const onDrag = (e) => {
    if (!dragging) return;
    e.preventDefault();
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;

    const maxOffset = (AVATAR_SIZE * (imgScale - 1)) / 2;
    const safeMaxOffset = Number.isNaN(maxOffset) ? 0 : maxOffset;
    const clamp = (v) => Math.max(Math.min(v, safeMaxOffset), -safeMaxOffset);

    setImgOffset((prev) => ({
      x: clamp(prev.x + dx),
      y: clamp(prev.y + dy),
    }));
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const endDrag = (e) => {
    if (!dragging) return;
    setDragging(false);

    if (e?.pointerId) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (err) {
        // ignore
      }
    }

    supabase
      .from('user_accounts')
      .update({
        profile_picture_offset_x: imgOffset.x,
        profile_picture_offset_y: imgOffset.y,
        profile_picture_scale: imgScale,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userid)
      .then(({ error }) => {
        if (error) console.error('Failed to save image offset:', error);
      })
      .catch((err) => console.error('Failed to save image offset:', err));
  };

  // Mirrors onboarding Step4Verification: encrypted with the same per-user
  // key so files uploaded in either place stay interchangeable.
  const handleSupportingDocUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setAlert('Document size should be less than 10MB', 'error');
      return;
    }
    setUploadingDoc(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${userid}/supporting/${fileName}`;
      const key = await deriveKey(userid);
      const encryptedBlob = await encryptFile(file, key, fileName);
      const { error: uploadError } = await supabase.storage
        .from('tikcare-attachments')
        .upload(filePath, encryptedBlob, { cacheControl: '3600', upsert: true });
      if (uploadError) throw uploadError;
      const { data: publicUrlData } = supabase.storage
        .from('tikcare-attachments')
        .getPublicUrl(filePath);
      const publicUrl = publicUrlData.publicUrl;
      const { error: dbError } = await supabase
        .from('user_accounts')
        .update({ supporting_document_url: publicUrl })
        .eq('id', userid);
      if (dbError) throw dbError;
      setSupportingDocUrl(publicUrl);
      setAlert('Supporting document uploaded.', 'success');
    } catch (error) {
      console.error('Error uploading supporting document:', error);
      setAlert('Failed to upload document. Please try again.', 'error');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setAlert('Please upload an image file', 'error');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setAlert('Image size should be less than 10MB', 'error');
      return;
    }

    setUploadingImage(true);

    try {
      // Upload new image
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${userid}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Error uploading image:', uploadError);
        setAlert('Failed to upload image', 'error');
        return;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);

      // Update database with new profile picture URL
      const { error: dbError } = await supabase
        .from('user_accounts')
        .update({ 
          profile_picture_url: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', userid);

      if (dbError) {
        console.error('Error updating profile picture in database:', dbError);
        try {
          await supabase.storage.from('profile-pictures').remove([filePath]);
        } catch (cleanupError) {
          console.warn('Could not remove the unsaved profile picture:', cleanupError);
        }
        setAlert('Failed to update profile picture', 'error');
        return;
      }

      setProfilePictureUrl(publicUrl);
      setImgOffset({ x: 0, y: 0 });
      setImgScale(1.0);
      setAlert('Profile picture uploaded successfully', 'success');

      // Keep the existing image until the replacement is uploaded and saved.
      if (profilePictureUrl) {
        const oldFileName = profilePictureUrl.split('/').pop().split('?')[0];
        try {
          const { error: cleanupError } = await supabase.storage.from('profile-pictures').remove([`${userid}/${oldFileName}`]);
          if (cleanupError) console.warn('Could not remove the previous profile picture:', cleanupError);
        } catch (cleanupError) {
          console.warn('Could not remove the previous profile picture:', cleanupError);
        }
      }
    } catch (error) {
      console.error('Unexpected error uploading image:', error);
      setAlert('An unexpected error occurred', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleProfileSubmit = async () => {
    if (savingRef.current || loading || loadError) return false;
    setSaveError('');
    if(!name) {
      setAlert('Please enter your name', 'error');
      return;
    }

    if (firstSessionPrice !== '' && Number(firstSessionPrice) < 10) {
      setAlert('First session price must be at least 10 HKD.', 'error');
      return;
    }

    if (regularSessionPrice !== '' && Number(regularSessionPrice) < 10) {
      setAlert('Regular session price must be at least 10 HKD.', 'error');
      return;
    }

    savingRef.current = true;
    setSavingProfile(true);
    let accountSaved = false;
    const submittedSections = sectionValues;
    const submittedSharing = canEnableSharing && shareProfile && (savedSharing || canEnableSavedSharing);
    const clinicLocationsWithTeamId = clinicLocations.map((location) => ({
      ...location,
      team_id: location.team_id || (location.location_id ? undefined : currentOrg?.id) || null
    }));
    try {
      const practiceData = {
        name: name,
        mode: mode,
        practicetype: practiceType,
        profile_picture_url: profilePictureUrl,
        about: about,
        practice_title: practiceTitle,
        organisation: organisation,
        country: country || null,
        university: university,
        address: address,
        phone_number: phoneNumber,
        website: website,
        email: useremail,
        secondary_email: secondaryEmail,
        gender: gender,
        languages: languages,
        intervention_approach: interventionApproach,
        assessment_types: assessmentTypes,
        preferred_case_types: preferredCaseTypes,
        client_groups: clientGroups,
        religion: religion === 'Other' ? otherReligion.trim() : religion,
        cases_to_avoid: casesToAvoid,
        professional_qualification: professionalQualification,
        service_formats: serviceFormats,
        consultation_software: consultationSoftware,
        first_session_duration: firstSessionDuration,
        first_session_price: firstSessionPrice,
        regular_session_duration: regularSessionDuration,
        regular_session_price: regularSessionPrice,
        bank_account: bankAccount,
        // Registration/certificate fields are persisted by the shared
        // ProfessionalVerificationForm; only the sharing decision lives here.
        share_profile: submittedSharing,
        clinic_locations: clinicLocationsWithTeamId,
        experience: experienceYears,
        profile_picture_offset_x: imgOffset.x,
        profile_picture_offset_y: imgOffset.y,
        profile_picture_scale: imgScale,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase.from('user_accounts').update(practiceData).eq('id', userid);
      if (error) {
        console.error('Error updating practice info:', error);
        throw error;
      }
      
      accountSaved = true;
      setSavedSections(submittedSections);
      setSavedSharing(submittedSharing);
      dispatch(setUserDetails({ id: userid, email: useremail, name, practicetype: practiceType, mode, data_retention_days }));

      // Update organization data in templatelayout table
      const organizationData = {
        name: organisation || "",
        email: secondaryEmail || "",
        phone: phoneNumber || "",
        address: address || "",
        practice_type: practiceType || "",
        practice_title: practiceTitle || "",
        website: website || ""
      };
      
      // Check if template layout record exists for this user
      const { data: existingTemplateData, error: templateCheckError } = await supabase
        .from('templatelayout')
        .select('id')
        .eq('id', userid)
        .maybeSingle();

      if (templateCheckError) {
        throw new Error('Profile was saved, but document settings could not be updated. Please retry.');
      }
      
      let templateError;
      
      if (existingTemplateData?.id) {
        // Update existing template layout record with new organization data
        const { error } = await supabase
          .from('templatelayout')
          .update({
            organization_data: JSON.stringify(organizationData),
            updated_at: new Date().toISOString()
          })
          .eq('id', userid);
        templateError = error;
      } else {
        // Create new template layout record with organization data
        const { error } = await supabase
          .from('templatelayout')
          .insert([{
            id: userid,
            header_layout: JSON.stringify(Array(15).fill(null)), // Empty header layout
            footer_layout: JSON.stringify(Array(15).fill(null)), // Empty footer layout
            logo_url: null,
            signature_url: null,
            custom_image1_url: null,
            custom_image2_url: null,
            organization_data: JSON.stringify(organizationData),
            updated_at: new Date().toISOString()
          }]);
        templateError = error;
      }
      
      if (templateError) {
        throw new Error('Profile was saved, but document settings could not be updated. Please retry.');
      }
      
      setAlert("Profile updated successfully!", 'success');
      return true;
    } catch (error) {
      console.error('Unexpected error updating profile:', error);
      setSaveError(error.message || 'Could not save your profile. Your edits are still here. Please retry.');
      setAlert(accountSaved ? 'Profile saved. Document settings could not be updated; please retry.' : 'Could not save your profile. Please retry.', 'error');
      return false;
    } finally {
      savingRef.current = false;
      setSavingProfile(false);
    }
  };

  const handlePracticeTypeChange = (event) => {
    setPracticeType(event.target.value);
  };

  // "Auto-fill from your web page": AI may only fill these fields, and only while
  // they're empty. Registry drafts go through the existing Verify flow;
  // ordinary profile fields use the normal Update save.
  const autofillFields = [
    { key: 'name', label: 'Your Name', section: sectionLabels[0], current: name, set: setName },
    { key: 'practice_type', label: 'Type/Speciality', section: sectionLabels[0], current: practiceType, set: setPracticeType },
    { key: 'about', label: 'About Me', section: sectionLabels[0], current: about, set: setAbout },
    { key: 'languages', label: 'Languages', section: sectionLabels[0], current: languages, set: setLanguages },
    { key: 'practice_title', label: 'Practice Title', section: sectionLabels[1], current: practiceTitle, set: setPracticeTitle },
    { key: 'organisation', label: 'Organization', section: sectionLabels[1], current: organisation, set: setOrganisation },
    { key: 'university', label: 'University', section: sectionLabels[1], current: university, set: setUniversity },
    { key: 'professional_qualification', label: 'Professional Qualification', section: sectionLabels[1], current: professionalQualification, set: setProfessionalQualification },
    { key: 'experience', label: 'Experience (years)', section: sectionLabels[1], current: experienceYears, set: setExperienceYears },
    ...registryPrefillFields(registryAutofillState),
    { key: 'intervention_approaches', label: 'Intervention Approach', section: sectionLabels[2], current: interventionApproach, set: setInterventionApproach },
    { key: 'assessment_types', label: 'Assessment Types', section: sectionLabels[2], current: assessmentTypes, set: setAssessmentTypes },
    { key: 'preferred_case_types', label: 'Speciality Areas', section: sectionLabels[2], current: preferredCaseTypes, set: setPreferredCaseTypes },
    { key: 'client_groups', label: 'Client Group', section: sectionLabels[2], current: clientGroups, set: setClientGroups },
    { key: 'service_formats', label: 'Service Format', section: sectionLabels[2], current: serviceFormats, set: setServiceFormats },
    { key: 'phone', label: 'Phone Number', section: sectionLabels[3], current: phoneNumber, set: setPhoneNumber },
    { key: 'address', label: 'Address', section: sectionLabels[3], current: address, set: setAddress },
    { key: 'website', label: 'Website', section: sectionLabels[3], current: website, set: setWebsite },
    { key: 'contact_email', label: 'Email', section: sectionLabels[3], current: secondaryEmail, set: setSecondaryEmail },
  ];
  const applyAutofill = (rows) => {
    const applied = [];
    rows.forEach(row => {
      const field = autofillFields.find(f => f.key === row.key);
      if (field?.set && isBlankValue(field.current)) { field.set(row.value); applied.push(row); }
    });
    return [...applied, ...(verificationRef.current?.applyAutofill(rows.filter(row => REGISTRY_KEYS.includes(row.key))) || [])];
  };
  // Undo clears only fields still holding the AI value, so later hand edits stay.
  const undoAutofill = (rows) => {
    verificationRef.current?.undoAutofill(rows);
    rows.forEach((row) => {
      const field = autofillFields.find((f) => f.key === row.key);
      if (field?.set && JSON.stringify(field.current) === JSON.stringify(row.value)) field.set(Array.isArray(row.value) ? [] : '');
    });
  };

  const startCustomInput = (field) => {
    setCustomInputActive((prev) => ({ ...prev, [field]: true }));
    setCustomInputValue((prev) => ({ ...prev, [field]: '' }));
  };
  const cancelCustomInput = (field) => {
    setCustomInputActive((prev) => ({ ...prev, [field]: false }));
    setCustomInputValue((prev) => ({ ...prev, [field]: '' }));
  };
  const selectDynamicOption = (type, label) => {
    const select = setter => setter(current => current.includes(label) ? current : [...current, label]);
    switch (type) {
      case DYNAMIC_OPTION_TYPES.INTERVENTION_APPROACHES: select(setInterventionApproach); break;
      case DYNAMIC_OPTION_TYPES.ASSESSMENT_TYPES: select(setAssessmentTypes); break;
      case DYNAMIC_OPTION_TYPES.PREFERRED_CASE_TYPES: select(setPreferredCaseTypes); break;
      case DYNAMIC_OPTION_TYPES.CLIENT_GROUPS: select(setClientGroups); break;
      case DYNAMIC_OPTION_TYPES.AVOID_CASE_TYPES: select(setCasesToAvoid); break;
      default: break;
    }
  };
  const findDynamicOption = (type, value) => {
    const selected = {
      [DYNAMIC_OPTION_TYPES.INTERVENTION_APPROACHES]: interventionApproach,
      [DYNAMIC_OPTION_TYPES.ASSESSMENT_TYPES]: assessmentTypes,
      [DYNAMIC_OPTION_TYPES.PREFERRED_CASE_TYPES]: preferredCaseTypes,
      [DYNAMIC_OPTION_TYPES.CLIENT_GROUPS]: clientGroups,
      [DYNAMIC_OPTION_TYPES.AVOID_CASE_TYPES]: casesToAvoid,
    };
    const normalized = value.trim().toLowerCase();
    return [...(optionsByType[type] || []), ...(selected[type] || [])].find(option =>
      option.trim().toLowerCase() === normalized || withAgeRange(option).trim().toLowerCase() === normalized
    );
  };
  const addCustomOption = (type, value) => {
    const trimmed = value?.trim();
    if (!trimmed || optionsLoading || optionsError) return;
    const existing = findDynamicOption(type, trimmed);
    if (existing === OTHER_OPTION) {
      startCustomInput(type);
      setDynamicSearchInput(prev => ({ ...prev, [type]: '' }));
      return;
    }
    const label = existing || trimmed;
    setOptionsByType(prev => ({ ...prev, [type]: [...new Set([...(prev[type] || []).filter(option => option !== OTHER_OPTION), label]), OTHER_OPTION] }));
    selectDynamicOption(type, label);
    cancelCustomInput(type);
    setDynamicSearchInput(prev => ({ ...prev, [type]: '' }));
  };
  const handleDynamicEnter = (type, event) => {
    if (event.key !== 'Enter' || event.target.tagName !== 'INPUT') return;
    // Enter commits the composition text when an IME is active.
    if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) {
      event.defaultMuiPrevented = true;
      return;
    }
    const value = event.target.value || '';
    // Preserve MUI's keyboard selection when an option was highlighted.
    if (event.target.getAttribute('aria-activedescendant') && !findDynamicOption(type, value)) return;
    event.preventDefault();
    event.defaultMuiPrevented = true;
    addCustomOption(type, value);
  };

  const handleDynamicSelectChange = (type, values) => {
    const raw = values && values.target ? values.target.value : values;
    const next = Array.isArray(raw) ? raw : raw ? [raw] : [];
    if (next.includes(OTHER_OPTION)) {
      startCustomInput(type);
      return;
    } else {
      switch (type) {
        case DYNAMIC_OPTION_TYPES.INTERVENTION_APPROACHES: setInterventionApproach(next); break;
        case DYNAMIC_OPTION_TYPES.ASSESSMENT_TYPES: setAssessmentTypes(next); break;
        case DYNAMIC_OPTION_TYPES.PREFERRED_CASE_TYPES: setPreferredCaseTypes(next); break;
        case DYNAMIC_OPTION_TYPES.CLIENT_GROUPS: setClientGroups(next); break;
        case DYNAMIC_OPTION_TYPES.AVOID_CASE_TYPES: setCasesToAvoid(next); break;
        default: break;
      }
    }
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };
  const handleChipReorder = (type, from, to) => {
    const apply = (setter, arr) => setter(reorder(arr || [], from, to));
    switch (type) {
      case DYNAMIC_OPTION_TYPES.INTERVENTION_APPROACHES: apply(setInterventionApproach, interventionApproach); break;
      case DYNAMIC_OPTION_TYPES.ASSESSMENT_TYPES: apply(setAssessmentTypes, assessmentTypes); break;
      case DYNAMIC_OPTION_TYPES.PREFERRED_CASE_TYPES: apply(setPreferredCaseTypes, preferredCaseTypes); break;
      case DYNAMIC_OPTION_TYPES.CLIENT_GROUPS: apply(setClientGroups, clientGroups); break;
      case DYNAMIC_OPTION_TYPES.AVOID_CASE_TYPES: apply(setCasesToAvoid, casesToAvoid); break;
      default: break;
    }
  };

  const handleChipRemove = (type, value) => {
    const remove = (setter, arr) => setter((arr || []).filter((v) => v !== value));
    switch (type) {
      case DYNAMIC_OPTION_TYPES.INTERVENTION_APPROACHES: remove(setInterventionApproach, interventionApproach); break;
      case DYNAMIC_OPTION_TYPES.ASSESSMENT_TYPES: remove(setAssessmentTypes, assessmentTypes); break;
      case DYNAMIC_OPTION_TYPES.PREFERRED_CASE_TYPES: remove(setPreferredCaseTypes, preferredCaseTypes); break;
      case DYNAMIC_OPTION_TYPES.CLIENT_GROUPS: remove(setClientGroups, clientGroups); break;
      case DYNAMIC_OPTION_TYPES.AVOID_CASE_TYPES: remove(setCasesToAvoid, casesToAvoid); break;
      default: break;
    }
  };

  const renderReorderableField = (type, values, label, tooltip, placeholder) => (
    <Box sx={{ width: '100%' }}>
      {/* Title with tooltip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoOutlinedIcon fontSize="small" sx={{ color: '#29C0BB' }} />
          </Tooltip>
        )}
      </Box>

      {/* Draggable chips */}
      {values && values.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {values.map((val, idx) => (
            <Box
              key={`${type}-${val}-${idx}`}
              draggable
              onDragStart={(e) => e.dataTransfer.setData('text/plain', idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const from = Number(e.dataTransfer.getData('text/plain'));
                const to = idx;
                if (!Number.isNaN(from)) handleChipReorder(type, from, to);
              }}
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 20,
                bgcolor: '#e0f7f5',
                border: '1px dashed #29C0BB',
                cursor: 'grab',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography variant="body2" sx={{ mr: 0.5 }}>
                {withAgeRange(val)}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleChipRemove(type, val)}
                sx={{ p: 0.3, color: '#29C0BB' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Selection dropdown - hidden when custom input is active */}
      {!customInputActive[type] && (
        <SearchableMultiSelect
          label=""
          options={optionsByType[type]}
          value={values}
          onChange={(e) => handleDynamicSelectChange(type, e)}
          getOptionLabel={withAgeRange}
          chipColor="#29C0BB"
          placeholder={placeholder}
          clearOnBlur={false}
          inputValue={dynamicSearchInput[type] || ''}
          onInputChange={(event, value) => setDynamicSearchInput(prev => ({ ...prev, [type]: value }))}
          onKeyDown={event => handleDynamicEnter(type, event)}
          helperText='Type a new option and press Enter. Custom options are saved for you when you update your profile.'
          disabled={optionsLoading || Boolean(optionsError)}
          endAdornment={optionsLoading ? <CircularProgress size={16} /> : null}
          size="small"
          hideTags
          showPlaceholderAlways
        />
      )}

      {/* Custom input UI */}
      {customInputActive[type] && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 0 }}>
          <TextField
            size="small"
            placeholder={`Enter custom ${label.toLowerCase()}`}
            value={customInputValue[type] || ''}
            disabled={optionsLoading || Boolean(optionsError)}
            onKeyDown={event => handleDynamicEnter(type, event)}
            onChange={(e) =>
              setCustomInputValue((p) => ({ ...p, [type]: e.target.value }))
            }
            fullWidth
            sx={{ flex: 1, minWidth: 0 }}
          />
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, '& .MuiButton-root': { minWidth: 80, height: 40, whiteSpace: 'nowrap' } }}>
          <Button
            variant="contained"
            onClick={() => addCustomOption(type, customInputValue[type])}
            disabled={optionsLoading || Boolean(optionsError) || !customInputValue[type]?.trim()}
            sx={{ 
              backgroundColor: '#29C0BB',
              '&:hover': { backgroundColor: '#1da8a3' }
            }}
          >
            Add
          </Button>
          <Button variant="outlined" disabled={optionsLoading || Boolean(optionsError)} onClick={() => cancelCustomInput(type)}>
            Cancel
          </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );

  return (
    <div style={{ width: '100%', minWidth: 0 }}>
      <ProfileGuide key={userid} userId={userid} ready={!loading && !loadError} />
      {(loading || loadError) && <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}><Button variant="outlined" onClick={onOpenAccountSettings}>Account settings</Button></Box>}
      <Grid container spacing={2}>
        <Grid size={12} sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start'}}>
          {
            loading ? (
              <Grid container spacing={2} sx={{ padding: '50px', height: '50vh', }} justifyContent="center" alignItems="center">
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Loader />
                </Box>
              </Grid>
            ) : loadError ? <Alert severity="error" action={<Button onClick={() => setLoadAttempt(value => value + 1)}>Retry</Button>}>{loadError}</Alert> : (
          <Stack spacing={3} width="100%" sx={{ minWidth: 0, containerType: 'inline-size' }} direction="column" alignItems="flex-start" justifyContent="center">
            <Box className="profile-page-header" data-tour="settings-update-profile">
              <Box><Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Settings / Profile &amp; Account</Typography><Typography component="h1" variant="h6" sx={{ fontWeight: 700, color: '#293d42' }}>Build your profile</Typography></Box>
              <Box className="profile-page-actions">
                <Button variant="outlined" onClick={onOpenAccountSettings}>Account settings</Button>
                <Button variant="outlined" onClick={() => setActiveSection(5)}>Preview profile</Button>
                <Button data-tour="profile-save" variant="contained" disabled={savingProfile || uploadingImage || uploadingDoc} onClick={() => { setProfileSubmitted(true); handleProfileSubmit(); }}>{savingProfile ? 'Saving…' : 'Update'}</Button>
              </Box>
            </Box>
            <Typography color="text.secondary">Your professional details, session fees and packages in one place.</Typography>
            <Box sx={{ width: '100%', boxSizing: 'border-box', bgcolor: '#edf5f4', borderRadius: 2, p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" spacing={2}><Typography fontWeight={600}>{savedCompleteCount} of 5 sections complete</Typography><Typography color="text.secondary">{savedSharing ? 'Shared' : 'Not shared'}{shareProfile !== savedSharing ? ' · Unsaved change' : ''}</Typography></Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{savedSharing ? 'Your saved profile is shared with MindVault clients.' : 'Complete and save each section to enable referrals.'}{missingShareProfileFields.length > 0 && ` ${missingShareProfileFields.length} fields left to fill.`}</Typography>
              <LinearProgress variant="determinate" value={savedCompleteCount * 20} sx={{ mt: 1.5, height: 5, borderRadius: 4, bgcolor: '#dce9e7', '& .MuiLinearProgress-bar': { bgcolor: '#70b6b3' } }} />
            </Box>
            <ProfileAutofill
              fields={autofillFields}
              optionLists={buildPrefillOptionLists(optionsByType)}
              onApply={applyAutofill}
              onUndo={undoAutofill}
              saveLabel="Update"
              appliedNote="Registry details are drafts: use Verify in Professional Qualifications to check and save them."
            />
            {saveError &&<Alert severity="error" sx={{ width: '100%' }}>{saveError}</Alert>}

<Box className="profile-section-layout">
  <Box role="tablist" aria-label="Profile sections" className="profile-section-navigation">
    {sectionLabels.map((label, index) => <Button key={label} id={`profile-tab-${index}`} role="tab" aria-selected={activeSection === index} tabIndex={activeSection === index ? 0 : -1} onKeyDown={event => {
      let next;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % sectionLabels.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index + sectionLabels.length - 1) % sectionLabels.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = sectionLabels.length - 1;
      if (next !== undefined) { event.preventDefault(); setActiveSection(next); document.getElementById(`profile-tab-${next}`)?.focus(); }
    }} aria-controls={`profile-panel-${index}`} onClick={() => setActiveSection(index)} className={activeSection === index ? 'selected' : ''}>
      <Box component="span" className="profile-section-number">{index < 5 && sectionComplete[index] && !sectionDirty[index] ? '✓' : index < 5 ? index + 1 : '↗'}</Box>
      <Box component="span">{label}{index < 5 && sectionDirty[index] ? ' •' : ''}</Box>
    </Button>)}
  </Box>
  <Box className="profile-section-card">
    <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, color: '#263c40', mb: 3 }}>{sectionLabels[activeSection]}</Typography>
<Stack id="profile-panel-0" role="tabpanel" aria-labelledby="profile-tab-0" data-profile-section="0" hidden={activeSection !== 0} spacing={3} sx={{ display: activeSection === 0 ? 'flex' : 'none', width: '100%', minWidth: 0 }}>
            {/* Profile Picture Section */}
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Profile Picture</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      border: '2px solid #e0e0e0',
                      cursor: dragging ? 'grabbing' : 'grab',
                      touchAction: 'none',
                      userSelect: 'none'
                    }}
                    onPointerDown={startDrag}
                    onPointerMove={onDrag}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onLostPointerCapture={endDrag}
                  >
                    {profilePictureUrl ? (
                      <img
                        src={profilePictureUrl}
                        alt="Profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          transform: `translate(${imgOffset.x}px, ${imgOffset.y}px) scale(${imgScale})`,
                          transformOrigin: 'center',
                          pointerEvents: 'none'
                        }}
                        draggable={false}
                        onLoad={() => setImageLoaded(true)}
                      />
                    ) : (
                      <Box sx={{ textAlign: 'center', color: '#999' }}>
                        <FileUploadIcon sx={{ fontSize: 40 }} />
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <Button
                      variant="contained"
                      component="label"
                      disabled={uploadingImage}
                      sx={{
                        backgroundColor: '#29C0BB',
                        '&:hover': { backgroundColor: '#1da8a3' },
                        textTransform: 'none',
                        borderRadius: 2
                      }}
                    >
                      {uploadingImage ? 'Uploading...' : 'Upload Picture'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </Button>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#666' }}>
                      Recommended: Square image, at least 400x400px
                    </Typography>

                    {profilePictureUrl && (
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1, width: 220 }}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Zoom</Typography>
                        <Slider
                          size="small"
                          min={1}
                          max={2}
                          step={0.05}
                          value={imgScale}
                          onChange={(_, v) => {
                            const next = Array.isArray(v) ? v[0] : v;
                            setImgScale(next);
                          }}
                        />
                      </Stack>
                    )}
                  </Box>
                </Box>
            </Box>

            {/* About Me Section */}
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                About Me
                <Typography component="span" variant="caption" sx={{ ml: 1, color: '#666' }}>
                  {about.length}/200 characters
                </Typography>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={about}
                onChange={(e) => {
                  const value = e.target.value;
                  // Always allow shortening so legacy texts saved under the old
                  // 200-word limit (often >200 chars) can still be edited down.
                  if (value.length <= 200 || value.length < about.length) {
                    setAbout(value);
                  } else {
                    // Clip over-long input (usually a paste) instead of dropping it.
                    setAbout(value.slice(0, 200));
                  }
                }}
                placeholder="Type Here..."
                sx={{ 
                  backgroundColor: '#f5f5f5',
                  '& .MuiOutlinedInput-root': { alignItems: 'baseline' }
                }}
                multiline
                minRows={4}
                maxRows={10}
                InputProps={{
                  inputProps: {
                    style: { resize: 'none' }
                  }
                }}
              />
            </Box>

            <TextField
              required
              fullWidth
              label="Your Name"
              variant="outlined"
              focused
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name" 
              sx={{ backgroundColor: '#f5f5f5', '& .MuiOutlinedInput-root': { alignItems: 'baseline' } }} 
              error={profileSubmitted && !name}
              helperText={profileSubmitted && !name && "Name is required"}
              multiline
              minRows={1}
              maxRows={10}
              InputProps={{
                inputProps: {
                  style: { resize: 'none' }
                }
              }}
            />

            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select
                labelId="gender-label"
                id="gender"
                value={gender}
                label="Gender"
                onChange={(e) => setGender(e.target.value)}
                sx={{ '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#29C0BB' } }}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="speciality-label">Type/Speciality</InputLabel>
              <Select
                labelId="speciality-label"
                id="speciality"
                value={practiceType || ''}
                label="Type/Speciality"
                onChange={handlePracticeTypeChange}
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#29C0BB' },
                }}
              >
                {filteredSpecialities.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Collected during onboarding (step 2); editable here so it
                stays in sync with the rest of the profile. */}
            <Autocomplete
              fullWidth
              options={COUNTRY_NAMES}
              value={country || null}
              onChange={(_, value) => setCountry(value || '')}
              renderInput={(params) => (
                <TextField {...params} label="Country / Region" sx={{ backgroundColor: '#f5f5f5' }} />
              )}
            />

            <Box sx={{ width: '100%' }}>
              <SearchableMultiSelect
                label="Languages"
                options={LANGUAGES}
                value={languages}
                onChange={(e) => setLanguages(e.target.value)}
                chipColor="#29C0BB"
                placeholder="Search or select languages..."
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel id="religion-label">Religion (optional)</InputLabel>
              <Select
                labelId="religion-label"
                label="Religion (optional)"
                value={religion}
                onChange={(e) => {
                  setReligion(e.target.value);
                  if (e.target.value !== 'Other') setOtherReligion('');
                }}
                sx={{ backgroundColor: '#f5f5f5' }}
              >
                <MenuItem value="">Not specified</MenuItem>
                {RELIGION_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {religion === 'Other' && (
              <TextField
                fullWidth
                label="Other religion (optional)"
                value={otherReligion}
                onChange={(e) => setOtherReligion(e.target.value)}
                placeholder="Enter religion"
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            )}


</Stack>
<Stack id="profile-panel-1" role="tabpanel" aria-labelledby="profile-tab-1" data-profile-section="1" hidden={activeSection !== 1} spacing={3} sx={{ display: activeSection === 1 ? 'flex' : 'none', width: '100%', minWidth: 0 }}>
            {/* Professional Information Section */}
            {
              showClinicalPractice && (
              <>
                <Typography variant="subtitle1" sx={{fontWeight: 'bold', color: '#29C0BB', mt: 2}}>
                  Professional Information
                </Typography>

                <TextField
                  fullWidth
                  label="Practice Title"
                  variant="outlined"
                  value={practiceTitle}
                  onChange={(e) => setPracticeTitle(e.target.value)}
                  placeholder="Enter your practice title"
                  sx={{ backgroundColor: '#f5f5f5', '& .MuiOutlinedInput-root': { alignItems: 'baseline' } }}
                  multiline
                  minRows={1}
                  maxRows={10}
                  InputProps={{
                    inputProps: {
                      style: { resize: 'none' }
                    }
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Organization"
                  variant="outlined"
                  value={organisation}
                  onChange={(e) => setOrganisation(e.target.value)}
                  placeholder="Enter your organization name"
                  sx={{ backgroundColor: '#f5f5f5', '& .MuiOutlinedInput-root': { alignItems: 'baseline' } }}
                  multiline
                  minRows={1}
                  maxRows={10}
                  InputProps={{
                    inputProps: {
                      style: { resize: 'none' }
                    }
                  }}
                />

                {/* Collected during onboarding (step 4); editable here so the
                    wizard is not the only place they can be set. */}
                <TextField
                  fullWidth
                  label="University"
                  multiline
                  minRows={2}
                  variant="outlined"
                  value={university}
                  onChange={(e) => setUniversity(e.target.value)}
                  placeholder="Degree and university — one entry per line"
                  sx={{ backgroundColor: '#f5f5f5' }}
                />

                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Supporting Document <Typography component="span" variant="caption" sx={{ color: '#8a94a0' }}>(Optional)</Typography>
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{
                      textTransform: 'none',
                      borderStyle: 'dashed',
                      borderColor: '#29C0BB',
                      color: '#29C0BB',
                      py: 2,
                      '&:hover': { borderColor: '#1da8a3', backgroundColor: '#e0f2f1' },
                    }}
                  >
                    {uploadingDoc ? <CircularProgress size={20} sx={{ color: '#29C0BB' }} /> : 'Upload document'}
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleSupportingDocUpload}
                      disabled={uploadingDoc}
                    />
                  </Button>
                  <Typography variant="caption" sx={{ color: '#8a94a0', display: 'block', mt: 0.5 }}>
                    PDF, JPG or PNG (Max 10MB)
                  </Typography>
                  {supportingDocUrl && (
                    <Typography variant="caption" sx={{ color: '#1da8a3', display: 'block', mt: 1 }}>
                      Supporting document uploaded.
                    </Typography>
                  )}
                </Box>

                {/* Registry verification (association, names, division number,
                    certificate, verify/unverify) — shared with onboarding step 4. */}
                <ProfessionalVerificationForm
                  ref={verificationRef}
                  onAutofillStateChange={setRegistryAutofillState}
                  userid={userid}
                  username={username}
                  useremail={useremail}
                  onStatusChange={handleVerificationStatus}
                />

                <TextField
                  fullWidth
                  label="Professional Qualification"
                  variant="outlined"
                  value={professionalQualification}
                  onChange={(e) => setProfessionalQualification(e.target.value)}
                  placeholder="Professional membership or qualification — one entry per line"
                  sx={{ backgroundColor: '#f5f5f5', '& .MuiOutlinedInput-root': { alignItems: 'baseline' } }}
                  multiline
                  minRows={1}
                  maxRows={10}
                  InputProps={{
                    inputProps: {
                      style: { resize: 'none' }
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Experience (years)"
                  variant="outlined"
                  type="number"
                  inputProps={{ min: 0, max: 80, step: 1 }}
                  value={experienceYears}
                  onChange={(e) => setExperienceYears(e.target.value)}
                  placeholder="Enter your years of experience"
                  sx={{ backgroundColor: '#f5f5f5', '& .MuiOutlinedInput-root': { alignItems: 'baseline' } }}
                />
              </>)
            }


</Stack>
<Stack id="profile-panel-2" role="tabpanel" aria-labelledby="profile-tab-2" data-profile-section="2" hidden={activeSection !== 2} spacing={3} sx={{ display: activeSection === 2 ? 'flex' : 'none', width: '100%', minWidth: 0 }}>
            {/* Clinical Practice Section */}
            {
              showClinicalPractice && (
              <>
                <Typography variant="subtitle1" sx={{fontWeight: 'bold', color: '#29C0BB', mt: 2}}>
                  Clinical Practice
                </Typography>
                {optionsError && <Alert severity="error" action={<Button onClick={fetchDynamicOptions}>Retry options</Button>}>{optionsError}</Alert>}

                {renderReorderableField(
                  DYNAMIC_OPTION_TYPES.INTERVENTION_APPROACHES,
                  interventionApproach,
                  'Intervention Approach',
                  'List intervention approaches from most relevant to least; this ordering is used for case referral.',
                  'Search or select approaches...'
                )}

                {renderReorderableField(
                  DYNAMIC_OPTION_TYPES.ASSESSMENT_TYPES,
                  assessmentTypes,
                  'Assessment Types',
                  'Order assessment types by relevance; used when matching assessments to your profile.',
                  'Select assessment types...'
                )}

                {renderReorderableField(
                  DYNAMIC_OPTION_TYPES.PREFERRED_CASE_TYPES,
                  preferredCaseTypes,
                  'Speciality Areas',
                  'Rank specialties from most to least relevant for better referrals.',
                  'Search or select case types...'
                )}

                {renderReorderableField(
                  DYNAMIC_OPTION_TYPES.CLIENT_GROUPS,
                  clientGroups,
                  'Client Group',
                  'Order client groups by relevance; used to match incoming cases.',
                  'Select client group...'
                )}

                {renderReorderableField(
                  DYNAMIC_OPTION_TYPES.AVOID_CASE_TYPES,
                  casesToAvoid,
                  'Client Group to Avoid',
                  'Rank groups to avoid so referrals can respect your preferences. This information is not shown to clients and is used only to improve the accuracy of AI matching.',
                  'Select client groups to avoid...'
                )}

                <Box sx={{ width: '100%' }}>
                  <SearchableMultiSelect
                    label="Service Format"
                    options={SERVICE_FORMAT_OPTIONS}
                    value={serviceFormats}
                    onChange={(e) => setServiceFormats(e.target.value)}
                    chipColor="#29C0BB"
                    placeholder="Select service formats..."
                  />
                </Box>

                {/* <Box sx={{ width: '100%' }}>
                  <SearchableMultiSelect
                    label="Consultation Software"
                    options={CONSULTATION_SOFTWARE}
                    value={consultationSoftware}
                    onChange={(e) => setConsultationSoftware(e.target.value)}
                    chipColor="#29C0BB"
                    placeholder="Select consultation software..."
                  />
                </Box> */}

              </>
              )
            }

</Stack>
<Stack id="profile-panel-3" role="tabpanel" aria-labelledby="profile-tab-3" data-profile-section="3" hidden={activeSection !== 3} spacing={3} sx={{ display: activeSection === 3 ? 'flex' : 'none', width: '100%', minWidth: 0 }}>
            {/* Contact Information Section */}
            <Typography variant="subtitle1" sx={{fontWeight: 'bold', color: '#29C0BB', mt: 2}}>
              Contact Information
            </Typography>
            
            <Typography variant="body2" color="text.secondary">Manage weekly working hours and time off in Appointments → My availability.</Typography>
            <ClinicLocationManager 
              clinicLocations={clinicLocations}
              onChange={setClinicLocations}
              availableLocations={availableLocations}
              showHours={false}
            />
            
            <TextField
              fullWidth
              label="Phone Number"
              variant="outlined"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Enter your contact number"
              sx={{ backgroundColor: '#f5f5f5', '& .MuiOutlinedInput-root': { alignItems: 'baseline' } }}
              multiline
              minRows={1}
              maxRows={10}
              InputProps={{
                inputProps: {
                  style: { resize: 'none' }
                }
              }}
            />

            <TextField
              fullWidth
              label="Address"
              variant="outlined"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter your practice address"
              sx={{ 
                backgroundColor: '#f5f5f5',
                '& .MuiOutlinedInput-root': {
                  alignItems: 'baseline'
                }
              }}
              multiline
              minRows={2}
              maxRows={10}
              InputProps={{
                inputProps: {
                  style: { resize: 'none' }
                }
              }}
            />
            
            <TextField
              fullWidth
              label="Website"
              variant="outlined"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="Enter your website URL"
              sx={{ backgroundColor: '#f5f5f5', '& .MuiOutlinedInput-root': { alignItems: 'baseline' } }}
              multiline
              minRows={1}
              maxRows={10}
              InputProps={{
                inputProps: {
                  style: { resize: 'none' }
                }
              }}
            />
            
            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              type="email"
              value={secondaryEmail}
              onChange={(e) => setSecondaryEmail(e.target.value)}
              placeholder="Enter your contact email"
              sx={{ backgroundColor: '#f5f5f5', '& .MuiOutlinedInput-root': { alignItems: 'baseline' } }}
              multiline
              minRows={1}
              maxRows={10}
              InputProps={{
                inputProps: {
                  style: { resize: 'none' }
                }
              }}
            />


</Stack>
<Stack id="profile-panel-4" role="tabpanel" aria-labelledby="profile-tab-4" data-profile-section="4" hidden={activeSection !== 4} spacing={3} sx={{ display: activeSection === 4 ? 'flex' : 'none', width: '100%', minWidth: 0 }}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>First Session</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Duration (minutes)"
                        type="number"
                        value={firstSessionDuration || ''}
                        onChange={(e) => setFirstSessionDuration(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{ min: 0, step: 5 }}
                        sx={{ backgroundColor: '#f5f5f5' }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Price (HKD)"
                        type="number"
                        value={firstSessionPrice || ''}
                        onChange={(e) => setFirstSessionPrice(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        error={Boolean(firstSessionPrice !== '' && Number(firstSessionPrice) < 10)}
                        helperText={
                          (firstSessionPrice !== '' && Number(firstSessionPrice) < 10)
                            ? "Price must be at least 10 HKD."
                            : ""
                        }
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ backgroundColor: '#f5f5f5' }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>Regular Session</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Duration (minutes)"
                        type="number"
                        value={regularSessionDuration || ''}
                        onChange={(e) => setRegularSessionDuration(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{ min: 0, step: 5 }}
                        sx={{ backgroundColor: '#f5f5f5' }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Price (HKD)"
                        type="number"
                        value={regularSessionPrice || ''}
                        onChange={(e) => setRegularSessionPrice(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        error={Boolean(regularSessionPrice !== '' && Number(regularSessionPrice) < 10)}
                        helperText={
                          (regularSessionPrice !== '' && Number(regularSessionPrice) < 10)
                            ? "Price must be at least 10 HKD."
                            : ""
                        }
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ backgroundColor: '#f5f5f5' }}
                      />
                    </Grid>
                  </Grid>
                </Box>


<Alert severity="info">Profile rates and packages save separately. Save your profile rates before saving packages. Existing purchases keep their original details.</Alert>
<SessionPackages embedded profileRate={regularSessionPrice} savedProfileRate={savedSections?.[4]?.regularSessionPrice} profileSaving={savingProfile} onDirtyChange={setPackagesDirty} />
            {/* Payout Setup — Stripe Connect onboarding (replaces the old manual bank field;
                Stripe collects bank details during its hosted onboarding flow) */}
            <PayoutSetup userid={userid} onStatusChange={setPayoutStatus} />
</Stack>
<Stack id="profile-panel-5" role="tabpanel" aria-labelledby="profile-tab-5" data-profile-section="5" hidden={activeSection !== 5} spacing={3} sx={{ display: activeSection === 5 ? 'flex' : 'none', width: '100%', minWidth: 0 }}>
            <Typography color="text.secondary" variant="body2">Preview your current profile as it appears in MindVault. Unsaved changes are shown here; ratings and live availability are shown in MindVault. Booking is disabled in this preview.</Typography>
            <MindVaultProfilePreview profile={{ name, profilePictureUrl, practiceTitle, university, professionalQualification, about, gender, teamName: (allOrgs || []).map(org => org.name).filter(Boolean).join(', ') || currentOrg?.name || currentOrg?.team_name || '', experienceYears, clinicLocations, languages, firstSessionPrice, firstSessionDuration, regularSessionPrice, regularSessionDuration, preferredCaseTypes, interventionApproach, assessmentTypes, clientGroups, serviceFormats, mode, registeredNumberVerified }} />
            {showShareProfileSection && (
              <Box data-tour="profile-sharing" sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Share my profile for case referral
                </Typography>
                <Tooltip
                  title={canEnableSavedSharing ? '' : 'Complete and save all five sections, save or discard package changes, and verify your registered number to enable referral sharing.'}
                >
                  <span>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={shareProfile && canEnableSharing}
                          onChange={(e) => setShareProfile(e.target.checked)}
                          disabled={!shareProfile && !canEnableSavedSharing}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#29C0BB' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#29C0BB' },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {(shareProfile && canEnableSharing) ? 'On — ' : 'Off — '}
                          Allow AI to match cases to me based on my specialty and availability
                        </Typography>
                      }
                    />
                  </span>
                </Tooltip>
                <Box data-tour="profile-requirements">
                {!canEnableSavedSharing && <Alert severity="info" sx={{ my: 2 }}>Complete and save all five sections before turning on sharing. Save or discard any package changes in Fees &amp; Packages.</Alert>}

                {!canEnableSharing && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
                      To enable referral sharing:
                    </Typography>
                    <Typography
                      variant="caption"
                      onClick={registeredNumberVerified ? undefined : () => {
                        setActiveSection(1);
                        requestAnimationFrame(() => document.getElementById('registered-number-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
                      }}
                      sx={{
                        display: 'block',
                        color: registeredNumberVerified ? '#2e7d32' : '#f44336',
                        cursor: registeredNumberVerified ? 'default' : 'pointer',
                        textDecoration: registeredNumberVerified ? 'none' : 'underline',
                      }}
                    >
                      {registeredNumberVerified ? '✓' : '✗'} Verify your registered number
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: profileCompleteForSharing ? '#2e7d32' : '#f44336' }}
                    >
                      {profileCompleteForSharing ? '✓' : '✗'} Complete your profile
                      {!profileCompleteForSharing && ` — missing: ${missingShareProfileFields.join(', ')}`}
                    </Typography>
                  </Box>
                )}
                {canEnableSavedSharing && <Typography variant="caption" color="success.main">Your profile meets the requirements for public visibility.</Typography>}
                </Box>
              </Box>
            )}


</Stack>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e8e8' }}>
      <Button disabled={activeSection === 0 || savingProfile} onClick={() => setActiveSection(value => value - 1)}>Previous</Button>
      <Button variant="contained" disabled={savingProfile || uploadingImage || uploadingDoc} onClick={async () => { setProfileSubmitted(true); if (await handleProfileSubmit()) setActiveSection(value => Math.min(5, value + 1)); }} sx={{ bgcolor: '#29C0BB' }}>{savingProfile ? 'Saving…' : activeSection === 5 ? 'Update Profile' : 'Save profile & continue'}</Button>
    </Stack>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Saves all profile fields. Packages have their own Save packages button.</Typography>
  </Box>
</Box>
          </Stack>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default UpdateProfile;
